import dayjs from "dayjs";
import { useState } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { useAuth } from "../context/AuthContext";
import weekOfYear from "dayjs/plugin/weekOfYear";
import { useQuery } from "@tanstack/react-query";
import Loader from "../components/Display/Loader";
import { getWeeklyWorkout } from "../api/workouts";
import useScreenSize from "../context/ScreenContext";
import MainCard from "../components/Layouts/MainCard";
import { CardTitle } from "../components/theme/texts";
import LoadingDice from "../components/Display/LoadingDice";
import WoWPerformance from "../components/Display/WoWPerformance";
import CountdownTimer from "../components/Display/CountDownTimer";
import RecentExercise from "../components/Display/RecentExercise";
import WeeklyActivity from "../components/Display/WeeklyActivity";
import BiometricsPanel from "../components/Display/BiometricsPanel";
import WodStandingsChart from "../components/charts/WodsStandingsChart";
import WeeklyWorkout from "../components/Display/weeklyworkout/WeeklyWorkout";
import WeeklyLeaderBoard from "../components/Display/weeklyworkout/WeeklyLeaderBoard";
import {
  scrollBar,
  SpacedRow,
  MainText,
  AllCenteredColumn,
} from "../data/style/Style";

const Dashboard = () => {
  dayjs.extend(weekOfYear);
  const { profile } = useAuth();
  const [level, setLevel] = useState(profile.level);
  const { mobileDevice, size } = useScreenSize();
  let [width] = size;

  const { data, isPending } = useQuery({
    queryKey: ["weekly", "workout", "level", profile.hasLevel, level],
    queryFn: ({ signal }) => getWeeklyWorkout({ level, signal }),
    staleTime: 1000 * 60 * 60 * 24,
  });

  return (
    <Stack height={"100%"} gap={4} overflow={"auto"} pr={1} sx={scrollBar}>
      <SpacedRow flexWrap={"wrap"} gap={4}>
        <Box flex={1} minWidth={"300px"}>
          <WeeklyActivity />
        </Box>
        <Box flex={1} minWidth={"300px"}>
          <BiometricsPanel />
        </Box>
        <Box flex={1} minWidth={"300px"}>
          <MainCard>
            <Stack alignItems={"center"} sx={{ minHeight: "300px" }}>
              {!isPending ? (
                <>
                  <SpacedRow width={"100%"} mb={1} alignItems={"center"}>
                    <CardTitle
                      subTitle={`Week ${dayjs().week()} · ${dayjs().year()}`}
                      mainTitle={"Workout of the week"}
                      isMobile={mobileDevice}
                    />
                    {data.workout_id !== 0 && (
                      <Box mr={1}>
                        <CountdownTimer targetTimestamp={data.expires} />
                      </Box>
                    )}
                  </SpacedRow>
                  {data.workout_id !== 0 && (
                    <WeeklyWorkout
                      wodId={data.workout_id}
                      level={level}
                      setLevel={setLevel}
                    />
                  )}
                  {data.workout_id === 0 && (
                    <AllCenteredColumn minHeight={"200px"} gap={3}>
                      <LoadingDice />
                      <MainText fontSize={mobileDevice ? "0.8rem" : "1rem"}>
                        Next workout generating soon…
                      </MainText>
                    </AllCenteredColumn>
                  )}
                </>
              ) : (
                <Loader minHeight={"300px"} />
              )}
            </Stack>
          </MainCard>
        </Box>
      </SpacedRow>
      <SpacedRow flexWrap={"wrap"} gap={4}>
        <Box flex={1} minWidth={"300px"}>
          <MainCard>
            <Stack gap={0.5}>
              <CardTitle
                subTitle={"Records"}
                mainTitle={"Exercise Progress"}
                isMobile={mobileDevice}
              />
              <RecentExercise type={3} />
              <RecentExercise type={2} />
              <RecentExercise type={4} />
            </Stack>
          </MainCard>
        </Box>
        <Box flex={width < 1000 ? 1 : 0.48} minWidth={"300px"}>
          <MainCard>
            <Stack sx={{ minHeight: "300px" }}>
              {!isPending ? (
                <WeeklyLeaderBoard workoutId={data.workout_id} level={level} />
              ) : (
                <Loader height={"100%"} minHeight={"300px"} />
              )}
            </Stack>
          </MainCard>
        </Box>
      </SpacedRow>
      <SpacedRow flexWrap={"wrap"} gap={4} pb={2}>
        <Box flex={1} minWidth={"300px"}>
          <WoWPerformance />
        </Box>
        <Box flex={1} minWidth={"300px"}>
          <MainCard>
            <Stack width={"100%"}>
              <Stack
                sx={{
                  height: "40vh",
                }}
              >
                <CardTitle
                  subTitle={"Chart"}
                  mainTitle={"Workout of the week recent standings"}
                  isMobile={mobileDevice}
                />
                <Box width={"100%"} height={"100%"}>
                  <WodStandingsChart />
                </Box>
              </Stack>
            </Stack>
          </MainCard>
        </Box>
      </SpacedRow>
    </Stack>
  );
};
export default Dashboard;
